import { Link } from "react-router-dom";
import { ArrowRight, Gift, Heart, MapPin, Repeat } from "lucide-react";
import { euro } from "@/lib/api";
import { SUB_PLANS } from "@/lib/subscription";
import { trackSubCta } from "@/components/subscription/SubOffers";

const F = SUB_PLANS.find((p) => p.id === "famille");
export const FAMILLE_PATH = "/abonnement?formule=famille#formules";
const MR = euro(F.ship.mondial_relay);
const DT = euro(F.ship.colissimo_domtom);
const COFFRET = "/editorial/coffret-vrais-produits-ozentya.webp";
const FAMILY = "/editorial/famille-abonnement.webp";

function FamilleCta({ placement, testid, className = "" }) {
  return (
    <Link to={FAMILLE_PATH} onClick={() => trackSubCta(placement, "famille")} data-testid={testid}
      className={`group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[var(--gold)] px-8 text-sm font-bold uppercase tracking-[0.2em] text-[var(--ink)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--gold-soft)] active:scale-[0.98] ${className}`}>
      Je compose mes 4 soins <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

const STEPS = [
  { n: "01", icon: Gift, title: "4 soins au choix", text: "Cheveux, locks, enfants, barbe ou corps : parmi nos soins à l'unité jusqu'à 16,99 €, le même soin peut être pris plusieurs fois." },
  { n: "02", icon: Heart, title: "Un soin pour chacun", text: "Maman, papa, les enfants : composez un colis qui couvre la routine de toute la maison, chaque mois." },
  { n: "03", icon: MapPin, title: "Livré près de chez vous", text: `Point Relais Mondial Relay ${MR} en France et en Europe, Colissimo Outre-mer ${DT} dans les DOM-TOM.` },
  { n: "04", icon: Repeat, title: "Libre et sans engagement", text: "Changez vos 4 soins avant chaque envoi, mettez en pause ou arrêtez en un clic depuis « Mon abonnement »." },
];

/** Grande mise en avant de l'Abonnement Famille (accueil). */
export function FamilleHomeSection({ placement = "home" }) {
  return (
    <section data-testid="home-famille-section" className="px-5 pt-20 md:px-10">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-sm border border-[var(--gold)]/50 bg-[var(--ink-2)]">
        <div className="relative">
          <img src={FAMILY} srcSet={`/editorial/famille-abonnement-800.webp 800w, ${FAMILY} 1264w`} sizes="(max-width: 800px) 100vw, 1152px" alt="Famille afro souriante réunie à la maison" width="1264" height="848" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover sm:aspect-[21/9]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink-2)] via-[var(--ink-2)]/20 to-transparent" />
          <span className="absolute left-4 top-4 rounded-full bg-[var(--gold)] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--ink)] md:left-8 md:top-8">Nouveau · Abonnement Famille</span>
        </div>
        <div className="grid gap-10 px-6 pb-10 sm:px-10 md:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] md:pb-14 lg:px-14">
          <div className="-mt-8 relative md:-mt-20">
            <p className="eyebrow mb-4">L'abonnement de toute la maison</p>
            <h2 data-testid="home-famille-title" className="font-display text-4xl leading-[1.02] sm:text-5xl lg:text-6xl">4 soins au choix, <span className="italic gold-text">chaque mois.</span></h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-[var(--parchment)]/90">Un colis Ozentya pour toute la famille : les cheveux de maman, la barbe de papa, le démêlage des enfants. Vous choisissez, on livre.</p>
            <ul className="mt-6 grid gap-2 text-sm text-[var(--parchment)]/90 sm:grid-cols-2">
              {["Soins jusqu'à 16,99 € chacun", "Modifiables avant chaque envoi", "France, Europe et DOM-TOM", "Sans engagement"].map((t) => <li key={t} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]" />{t}</li>)}
            </ul>
          </div>
          <div data-testid="home-famille-price" className="self-end rounded-sm border border-[var(--line)] bg-[var(--ink)] p-6">
            <p className="font-display text-5xl leading-none text-[var(--gold-soft)] sm:text-6xl">{euro(F.price)}<span className="whitespace-nowrap text-xl text-[var(--parchment)]/80"> / mois</span></p>
            <p className="mt-3 text-sm font-semibold text-[var(--gold-soft)]">Jusqu'à {euro(F.maxSave)} économisés chaque mois par rapport aux prix à l'unité</p>
            <p data-testid="home-famille-shipping" className="mt-3 text-sm text-[var(--parchment)]/90">Livraison en supplément : <b>{MR}</b> en Point Relais (France et Europe), <b>{DT}</b> en DOM-TOM.</p>
            <FamilleCta placement={placement} testid="home-famille-cta" className="mt-6 w-full" />
          </div>
        </div>
      </div>
    </section>
  );
}

/** Les 4 temps forts de la formule. */
export function FamilleSteps({ wide = true }) {
  return (
    <div data-testid="famille-steps" className={`grid gap-px overflow-hidden rounded-sm border border-[var(--line)] bg-[var(--line)] sm:grid-cols-2 ${wide ? "lg:grid-cols-4" : ""}`}>
      {STEPS.map(({ n, icon: Icon, title, text }) => (
        <div key={n} data-testid={`famille-step-${n}`} className="bg-[var(--ink)] p-6">
          <div className="flex items-center justify-between"><span className="font-display text-3xl text-[var(--gold-soft)]">{n}</span><Icon className="h-6 w-6 text-[var(--gold)]" strokeWidth={1.4} /></div>
          <h3 className="mt-5 font-display text-2xl leading-tight">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-[var(--parchment)]/85">{text}</p>
        </div>
      ))}
    </div>
  );
}

/** Encart compact (boutique, fiches produit, page abonnement). */
export function FamillePromo({ placement, testid, className = "mt-8" }) {
  return (
    <Link to={FAMILLE_PATH} onClick={() => trackSubCta(placement, "famille")} data-testid={testid}
      className={`group flex items-center gap-4 rounded-sm border-2 border-[var(--gold)] bg-[rgba(217,180,106,0.08)] p-4 transition-colors hover:bg-[rgba(217,180,106,0.14)] ${className}`}>
      <img src={COFFRET} alt="" aria-hidden="true" width="96" height="96" loading="lazy" className="h-20 w-20 shrink-0 rounded-sm object-cover sm:h-24 sm:w-24" />
      <span className="flex-1 text-sm">
        <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[var(--gold-soft)]">Nouveau · Abonnement Famille</span>
        <span className="mt-1 block font-display text-xl leading-tight">4 soins au choix chaque mois : {euro(F.price)} / mois</span>
        <span className="block text-xs text-[var(--parchment)]/85">Jusqu'à {euro(F.maxSave)} économisés · + {MR} de livraison en Point Relais ({DT} DOM-TOM) · sans engagement</span>
      </span>
      <ArrowRight className="h-5 w-5 shrink-0 text-[var(--gold-soft)] transition-transform group-hover:translate-x-1" />
    </Link>
  );
}
